import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Button, message } from 'antd';
import { 
  Type, 
  Tag as TagIcon, 
  FileText, 
  StickyNote,
  X
} from 'lucide-react';
import { Scene } from '../../../../types/scene';
import { SceneTagSelector } from '../scenes/SceneCard/SceneTagSelector';
import { SceneTagType } from '../../../../types/template';
import './EditSettingsModal.scss';

interface TagsFormControlProps {
  value?: SceneTagType[];
  onChange?: (tags: SceneTagType[]) => void;
  maxTags?: number;
}

const TagsFormControl: React.FC<TagsFormControlProps> = ({ value = [], onChange, maxTags = 3 }) => {
  return (
    <SceneTagSelector
      selectedTags={value}
      onChange={(tags) => onChange?.(tags)}
      maxTags={maxTags}
    />
  );
};

interface EditSettingsModalProps {
  visible: boolean;
  scene: Scene | null;
  onConfirm: (sceneId: string, updates: Partial<Scene>) => void;
  onCancel: () => void;
}

export const EditSettingsModal: React.FC<EditSettingsModalProps> = ({
  visible,
  scene,
  onConfirm,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (scene && visible) {
      form.setFieldsValue({
        title: scene.title,
        suggestedDuration: scene.suggestedDuration,
        tags: scene.tags || [],
        notes: scene.notes || '',
        script: scene.script || '',
      });
    }
  }, [scene, visible, form]);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      if (!scene) return;

      const updates: Partial<Scene> = {
        title: values.title,
        suggestedDuration: values.suggestedDuration,
        tags: values.tags,
        notes: values.notes,
        script: values.script,
      };

      await onConfirm(scene.id, updates);
      message.success('Scene settings updated');
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={560}
      className="edit-settings-modal"
      closable={false}
    >
      <div className="modal-header">
        <div className="header-content">
          <h2 className="modal-title">Edit Scene Settings</h2>
          {scene && (
            <span className="scene-name">{scene.title}</span>
          )}
        </div>
        <button className="close-button" onClick={handleCancel} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div className="modal-body">
        <Form
          form={form}
          layout="vertical"
          className="edit-settings-form"
        >
          <section className="settings-card">
            <div className="card-header">
              <div className="icon-wrapper">
                <Type size={16} />
              </div>
              <span className="card-title">General</span>
            </div>
            <div className="card-content">
              <Form.Item
                label="Scene Title"
                name="title"
                rules={[{ required: true, message: 'Please enter a scene title' }]}
              >
                <Input placeholder="Enter scene title" maxLength={150} />
              </Form.Item>

              <Form.Item
                label="Suggested Duration"
                name="suggestedDuration"
                extra="Recommended length for this scene in seconds"
              >
                <InputNumber
                  min={0}
                  max={3600}
                  placeholder="e.g., 30"
                  addonAfter="seconds"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </section>

          <section className="settings-card">
            <div className="card-header">
              <div className="icon-wrapper">
                <TagIcon size={16} />
              </div>
              <span className="card-title">Tags</span>
            </div>
            <div className="card-content">
              <Form.Item 
                name="tags" 
                extra="Add up to 3 tags to categorize this scene"
              >
                <TagsFormControl maxTags={3} />
              </Form.Item>
            </div>
          </section>

          <section className="settings-card">
            <div className="card-header">
              <div className="icon-wrapper">
                <FileText size={16} />
              </div>
              <span className="card-title">Script</span>
            </div>
            <div className="card-content">
              <Form.Item
                name="script"
                extra="Text displayed in the teleprompter during recording"
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter teleprompter script..."
                  showCount
                  maxLength={5000}
                />
              </Form.Item>
            </div>
          </section>

          <section className="settings-card">
            <div className="card-header">
              <div className="icon-wrapper">
                <StickyNote size={16} />
              </div>
              <span className="card-title">Notes</span>
            </div>
            <div className="card-content">
              <Form.Item
                name="notes"
                extra="Private notes and coaching tips for this scene"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Add scene notes or coaching tips..."
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </div>
          </section>
        </Form>
      </div>

      <div className="modal-footer">
        <Button 
          onClick={handleCancel} 
          disabled={loading}
          className="cancel-button"
        >
          Cancel
        </Button>
        <Button 
          type="primary" 
          onClick={handleConfirm} 
          loading={loading}
          className="save-button"
        >
          Save Changes
        </Button>
      </div>
    </Modal>
  );
};
